import { AxiosResponse } from "axios";
import {
  ICreateSolicitacaoOrcamentoAdminPayload,
  IPrecificacaoOrcamento,
  ISolicitacaoOrcamento,
  StatusSolicitacaoOrcamento,
} from "interfaces/orcamento";
import api from "./api";

function ensureSuccess<T>(response: AxiosResponse<T>): T {
  if (response.status >= 400) {
    const data = response.data as { message?: string };
    throw new Error(data?.message || "Falha na comunicação com o servidor");
  }
  return response.data;
}

function normalizeError(error: unknown) {
  const err = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return new Error(
    err?.response?.data?.message ||
      err?.message ||
      "Falha na comunicação com o servidor"
  );
}

export async function listarSolicitacoesOrcamento(params?: {
  status?: StatusSolicitacaoOrcamento;
  busca?: string;
}): Promise<ISolicitacaoOrcamento[]> {
  try {
    const response = await api.get<ISolicitacaoOrcamento[]>(
      "/orcamento/solicitacoes/admin",
      { params }
    );
    const data = ensureSuccess(response);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function criarSolicitacaoOrcamentoAdmin(
  payload: ICreateSolicitacaoOrcamentoAdminPayload
): Promise<{
  message: string;
  codigo: string;
  id: number;
  solicitacao: ISolicitacaoOrcamento;
}> {
  try {
    const response = await api.post<{
      message: string;
      codigo: string;
      id: number;
      solicitacao: ISolicitacaoOrcamento;
    }>("/orcamento/solicitacoes/admin", payload);
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function excluirSolicitacaoOrcamento(
  id: number
): Promise<{ message: string; id: number; codigo: string }> {
  try {
    const response = await api.delete<{
      message: string;
      id: number;
      codigo: string;
    }>(`/orcamento/solicitacoes/${id}`);
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function obterSolicitacaoOrcamento(
  id: number
): Promise<ISolicitacaoOrcamento> {
  try {
    const response = await api.get<ISolicitacaoOrcamento>(
      `/orcamento/solicitacoes/${id}`
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function obterPrecificacaoOrcamento(
  id: number
): Promise<IPrecificacaoOrcamento> {
  try {
    const response = await api.get<IPrecificacaoOrcamento>(
      `/orcamento/solicitacoes/${id}/precificacao`
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function atualizarSolicitacaoOrcamento(
  id: number,
  payload: {
    status?: StatusSolicitacaoOrcamento;
    obsInterna?: string | null;
    responsavelId?: string | null;
    valorProposto?: number | null;
  }
): Promise<ISolicitacaoOrcamento> {
  try {
    const response = await api.patch<ISolicitacaoOrcamento>(
      `/orcamento/solicitacoes/${id}`,
      payload
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function atualizarItensSolicitacaoOrcamento(
  id: number,
  payload: {
    itens: {
      id?: number;
      servicoId?: number;
      quantidade?: number;
      valorUnitario?: number;
      observacao?: string | null;
    }[];
    atualizarPrecosCatalogo?: boolean;
  }
): Promise<IPrecificacaoOrcamento> {
  try {
    const response = await api.patch<IPrecificacaoOrcamento>(
      `/orcamento/solicitacoes/${id}/itens`,
      payload
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function atualizarStatusSolicitacaoOrcamento(
  id: number,
  payload: { status: StatusSolicitacaoOrcamento; observacao?: string | null }
): Promise<ISolicitacaoOrcamento> {
  try {
    const response = await api.patch<ISolicitacaoOrcamento>(
      `/orcamento/solicitacoes/${id}/status`,
      payload
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function enviarPropostaOrcamento(
  id: number,
  payload: {
    valorProposto?: number;
    mensagemEmail?: string | null;
    obsInterna?: string | null;
    atualizarPrecosCatalogo?: boolean;
  }
): Promise<{
  message: string;
  valorCalculado: number;
  valorProposto: number;
  solicitacao: ISolicitacaoOrcamento;
}> {
  try {
    const response = await api.post<{
      message: string;
      valorCalculado: number;
      valorProposto: number;
      solicitacao: ISolicitacaoOrcamento;
    }>(`/orcamento/solicitacoes/${id}/enviar-proposta`, payload);
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

async function parseBlobError(data: Blob): Promise<string> {
  try {
    const text = await data.text();
    const json = JSON.parse(text) as { message?: string; error?: string };
    return json.message || json.error || "Falha ao baixar PDF";
  } catch {
    return "Falha ao baixar PDF";
  }
}

export async function baixarPropostaOrcamentoPdf(
  id: number,
  options: {
    codigo: string;
    valorProposto?: number;
    mensagem?: string;
  }
): Promise<void> {
  try {
    const response = await api.get(`/orcamento/solicitacoes/${id}/proposta-pdf`, {
      params: {
        ...(options.valorProposto != null
          ? { valorProposto: options.valorProposto }
          : {}),
        ...(options.mensagem ? { mensagem: options.mensagem } : {}),
      },
      responseType: "blob",
    });

    if (response.status >= 400) {
      throw new Error(await parseBlobError(response.data as Blob));
    }

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `proposta-${options.codigo}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw normalizeError(error);
  }
}
